import {
  Group,
  Mesh,
  PlaneGeometry,
  SphereGeometry,
  ShaderMaterial,
  Color,
  TorusGeometry,
  MeshBasicMaterial,
  DoubleSide,
} from 'three';
import { COLORS } from '../constants';
import type { TaskState, TaskType } from '../types';

// Task type to color mapping
const TASK_COLORS: Record<TaskType, number> = {
  'color-sequence': 0xa78bfa,  // purple
  'shape-match': 0x4ecdc4,     // teal
  'pattern-grid': 0x60a5fa,    // blue
  'signal-flow': 0xfb923c,     // orange
  'symmetry': 0xffe66d,        // gold
};

// Task type to symbol
const TASK_SYMBOLS: Record<TaskType, string> = {
  'color-sequence': 'CS',
  'shape-match': 'SM',
  'pattern-grid': 'PG',
  'signal-flow': 'SF',
  'symmetry': 'SY',
};

const cardVertShader = `
  uniform float uTime;
  uniform float uHover;
  varying vec2 vUv;
  varying float vFresnel;

  void main() {
    vUv = uv;
    vec3 pos = position;
    // Subtle float animation
    pos.y += sin(uTime * 2.0 + position.x * 3.0) * 0.02;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    vec3 viewDir = normalize(-mvPosition.xyz);
    vec3 norm = normalize(normalMatrix * normal);
    vFresnel = pow(1.0 - abs(dot(viewDir, norm)), 2.0);

    // Scale up on hover
    pos *= 1.0 + uHover * 0.15;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const cardFragShader = `
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uUrgency;
  uniform float uHover;
  uniform float uSelected;
  varying vec2 vUv;
  varying float vFresnel;

  void main() {
    // Base card color with scanlines
    float scanline = sin(vUv.y * 40.0 + uTime * 1.0) * 0.5 + 0.5;
    scanline = pow(scanline, 8.0) * 0.15;

    vec3 color = uColor * (0.3 + scanline);

    // Edge glow
    float edgeX = smoothstep(0.0, 0.08, vUv.x) * smoothstep(1.0, 0.92, vUv.x);
    float edgeY = smoothstep(0.0, 0.08, vUv.y) * smoothstep(1.0, 0.92, vUv.y);
    float edge = 1.0 - edgeX * edgeY;
    color += uColor * edge * 0.8;

    // Fresnel rim
    color += uColor * vFresnel * 0.5;

    // Urgency pulse (faster as urgency decreases)
    float pulseSpeed = mix(1.0, 6.0, 1.0 - uUrgency);
    float pulse = sin(uTime * pulseSpeed) * 0.5 + 0.5;
    float urgencyGlow = (1.0 - uUrgency) * pulse * 0.3;
    color += vec3(1.0, 0.3, 0.2) * urgencyGlow;

    // Hover brighten
    color *= 1.0 + uHover * 0.5;

    // Selection glow
    color += uColor * uSelected * 0.4 * (sin(uTime * 4.0) * 0.5 + 0.5);

    float alpha = 0.7 + edge * 0.2 + uHover * 0.1;
    gl_FragColor = vec4(color, alpha);
  }
`;

export class TaskRenderer {
  static createTaskMesh(task: TaskState): Group {
    const group = new Group();
    group.userData.taskId = task.id;

    const color = TASK_COLORS[task.type] || COLORS.taskQueue;

    // Invisible hitbox — large sphere so clicking is easy from any angle
    const hitboxGeo = new SphereGeometry(0.7, 8, 8);
    const hitboxMat = new MeshBasicMaterial({ visible: false });
    const hitbox = new Mesh(hitboxGeo, hitboxMat);
    hitbox.position.y = 0;
    hitbox.name = 'hitbox';
    group.add(hitbox);

    // Card plane
    const cardGeo = new PlaneGeometry(0.6, 0.8);
    const cardMat = new ShaderMaterial({
      vertexShader: cardVertShader,
      fragmentShader: cardFragShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(color) },
        uUrgency: { value: 1.0 },
        uHover: { value: 0 },
        uSelected: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
    });
    const card = new Mesh(cardGeo, cardMat);
    card.name = 'card';
    group.add(card);

    // Urgency ring at the bottom of the card
    const ringGeo = new TorusGeometry(0.2, 0.015, 8, 32);
    const ringMat = new MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.6,
    });
    const ring = new Mesh(ringGeo, ringMat);
    ring.position.y = -0.5;
    ring.rotation.x = -Math.PI / 2;
    ring.name = 'urgencyRing';
    group.add(ring);

    // Priority indicator (gold border for priority tasks)
    if (task.priority) {
      const priorityGeo = new PlaneGeometry(0.68, 0.88);
      const priorityMat = new MeshBasicMaterial({
        color: COLORS.success,
        transparent: true,
        opacity: 0.3,
        side: DoubleSide,
      });
      const priorityBorder = new Mesh(priorityGeo, priorityMat);
      priorityBorder.position.z = -0.01;
      priorityBorder.name = 'priorityBorder';
      group.add(priorityBorder);
    }

    return group;
  }

  static updateTaskVisuals(task: TaskState, time: number): void {
    if (!task.mesh) return;

    // The mesh is actually a Group stored in task.mesh — but we store Groups in task entities
    // For tasks we store the group directly
    const group = task.mesh.parent as Group;
    if (!group) return;

    const card = group.getObjectByName('card') as Mesh;
    const urgencyRing = group.getObjectByName('urgencyRing') as Mesh;

    if (!card) return;

    const mat = card.material as ShaderMaterial;
    const urgencyRatio = Math.max(0, task.urgency / 15); // normalized 0-1

    mat.uniforms.uTime.value = time;
    mat.uniforms.uUrgency.value = urgencyRatio;

    // Urgency ring scale (shrinks as time runs out)
    if (urgencyRing) {
      urgencyRing.scale.setScalar(urgencyRatio);
      const ringMat = urgencyRing.material as MeshBasicMaterial;
      ringMat.opacity = 0.3 + (1 - urgencyRatio) * 0.5;
      if (urgencyRatio < 0.3) {
        ringMat.color.setHex(0xff6b6b); // red when urgent
      }
    }

    // Card float animation
    group.position.y = 1.5 + Math.sin(time * 1.5 + group.position.x * 2) * 0.1;
    group.rotation.y = Math.sin(time * 0.3 + group.position.x) * 0.1;
  }

  static setHover(group: Group, hover: boolean): void {
    const card = group.getObjectByName('card') as Mesh;
    if (!card) return;
    const mat = card.material as ShaderMaterial;
    mat.uniforms.uHover.value = hover ? 1.0 : 0.0;
  }

  static setSelected(group: Group, selected: boolean): void {
    const card = group.getObjectByName('card') as Mesh;
    if (!card) return;
    const mat = card.material as ShaderMaterial;
    mat.uniforms.uSelected.value = selected ? 1.0 : 0.0;
  }
}
